// The faction marks: the faction second channel (R1568, mockup round 075,
// ui-map-interface.md §11).
//
// The faction colour was the only channel that named a faction on the map.
// Under protanopia the Rust Fleet and the Kelp Syndicate fall to ΔE 16 (two
// yellows), and under deuteranopia three more pairs fall under 30 (R1558).
// The owner picked a SHAPE beside the ink, four times over:
//
//  - card 01: the faction charge on a badge beside a hold and an
//    installation of another faction (`sites.ts`, baked into the cell);
//  - card 02: the same badge as a pip astern of a hull of another faction
//    (`fleets.ts`, one baked cell per charge and reading);
//  - card 04 row a: a dash pattern per faction on the line pass of the
//    territory border, on the traced outline (`shaders/geo.ts`, R1577),
//    from one table of uniforms;
//  - card 05: the key of the five banners (`ui/chart/faction-key.ts`).
//
// Every mark reads ONE table, `FACTION_MARKS` of
// `data/presentation/factionmarks.toml`, and the badge is ONE function
// here, so the key and the map cannot drift apart. The palette does not
// change: the ink of every mark is the faction ink (themes.md §10).
//
// This module is a leaf (`leaves.test.ts`): it draws through `MarkGraphics`
// and names the batch sinks by type alone.

import { FACTION_MARKS, PALETTE_TOKENS } from '../gen/presentation';
import { charge } from './chart-glyphs';
import { packChannels } from './color';
import { quadratic, type MarkGraphics } from './marks';
import type { CircleSink, SegmentSink } from '../fast/layers/batches';
import type { CameraUniforms } from '../fast/layer';
import type { HoldEra } from './hold-marks';

/**
 * The ground of a badge: the chrome panel ink of the palette, the card's
 * `INKBG`. The canvas is never themed (R1007), so it is the first paint.
 */
export const BADGE_GROUND: number = packChannels(PALETTE_TOKENS['--panel-rgb']);

/**
 * A `MarkGraphics` as the two sinks `charge()` draws into.
 *
 * The charge is a glyph of `chart-glyphs.ts`, and the heraldry piece draws
 * it through the instanced batches. A badge is BAKED, so the same glyph
 * strokes go through a path surface here. Every stroke takes a round cap,
 * as the card's canvas strokes do. Every call paints at once.
 */
class MarkSink implements SegmentSink, CircleSink {
  count = 0;
  globalAlpha = 1;

  constructor(private readonly g: MarkGraphics) {}

  begin(): void { this.count = 0; }

  flush(_cam: CameraUniforms): number { return this.count; }

  line(
    x0: number, y0: number, x1: number, y1: number,
    width: number, color: number, alpha: number,
  ): void {
    this.g.moveTo(x0, y0).lineTo(x1, y1)
      .stroke({ width, color, alpha: alpha * this.globalAlpha, cap: 'round', join: 'round' });
    this.count++;
  }

  ring(x: number, y: number, r: number, width: number, color: number, alpha: number): void {
    this.g.circle(x, y, r).stroke({ width, color, alpha: alpha * this.globalAlpha });
    this.count++;
  }

  disc(x: number, y: number, r: number, color: number, alpha: number): void {
    this.g.circle(x, y, r).fill({ color, alpha: alpha * this.globalAlpha });
    this.count++;
  }

  arc(
    x: number, y: number, r: number, width: number,
    start: number, sweep: number, color: number, alpha: number,
  ): void {
    this.g.moveTo(x + Math.cos(start) * r, y + Math.sin(start) * r)
      .arc(x, y, r, start, start + sweep)
      .stroke({ width, color, alpha: alpha * this.globalAlpha, cap: 'round' });
    this.count++;
  }

  dashed(
    x: number, y: number, r: number, width: number,
    count: number, fill: number, color: number, alpha: number,
    start = 0, sweep = Math.PI * 2,
  ): void {
    const n = Math.max(1, Math.round(count));
    const step = sweep / n;
    for (let k = 0; k < n; k++) {
      this.arc(x, y, r, width, start + k * step, step * fill, color, alpha);
    }
  }
}

/**
 * The heater shield of the historical reading (R548 card 23, as round 075
 * draws it): a chief, two flanks and two curves to the point. `w` is the
 * width, and the height is `w · shield_aspect`.
 */
export function shieldPoints(x: number, y: number, w: number): number[] {
  const h = w * FACTION_MARKS.shieldAspect;
  const l = x - w / 2, r = x + w / 2, t = y - h / 2, b = y + h / 2;
  const flank = y - h * 0.02, bend = y + h * 0.34;
  const pts = [l, t, r, t, r, flank];
  quadratic(pts, r, flank, r, bend, x, b, 8);
  quadratic(pts, x, b, l, bend, l, flank, 8);
  return pts;
}

/**
 * One faction badge, centred on `(x, y)`: the faction charge on a roundel
 * `size` across (modern) or on a heater shield `size` wide (historical).
 *
 * `chargeWord` is `FACTIONS[i].charge` and `ink` the faction colour. The
 * ground is the panel ink, the rim and the charge are the faction ink.
 */
export function drawFactionBadge(
  g: MarkGraphics, era: HoldEra, chargeWord: string, ink: number,
  x: number, y: number, size: number,
): void {
  const m = FACTION_MARKS;
  const sink = new MarkSink(g);
  if (era === 'h') {
    const pts = shieldPoints(x, y, size);
    g.poly(pts).fill({ color: BADGE_GROUND, alpha: m.groundAlphaHistorical });
    g.poly(pts).stroke({ width: m.rimWidth, color: ink, alpha: m.rimAlpha, join: 'round' });
    const h = size * m.shieldAspect;
    charge(chargeWord, sink, sink, x, y - h * m.shieldChargeLift,
      size * m.chargeShareHistorical, ink, m.chargeAlpha, m.chargeWidth);
    return;
  }
  g.circle(x, y, size / 2).fill({ color: BADGE_GROUND, alpha: m.groundAlphaModern });
  g.circle(x, y, size / 2).stroke({ width: m.rimWidth, color: ink, alpha: m.rimAlpha });
  charge(chargeWord, sink, sink, x, y, size * m.chargeShareModern, ink, m.chargeAlpha, m.chargeWidth);
}

/** Where a hold badge stands against the middle of its symbol (card 01). */
export function siteBadgeAt(era: HoldEra): readonly [number, number] {
  const s = FACTION_MARKS.site;
  return era === 'h' ? s.holdHistorical : s.holdModern;
}

/**
 * Does a hull of `faction` wear the pip for a viewer of `viewer` (card 02)?
 *
 * Only a hull of ANOTHER faction does: an own hull and a hull of the
 * viewer's faction carry none. A viewer with no seat (0) has no faction of
 * their own, so every hull is another faction's, which is the rule the site
 * layer keeps for a hold (`other`). A hull whose faction the wire does not
 * name (0) has no charge to show.
 */
export function wearsPip(faction: number, viewer: number, own: boolean): boolean {
  return !own && faction !== 0 && faction !== viewer;
}

// ---------------------------------------------------------------------------
// Card 04 row a: the dash of the territory edge
// ---------------------------------------------------------------------------

/** The dash pattern of one faction's border line, or `[]` for a solid one. */
export function dashPattern(faction: number): readonly number[] {
  return FACTION_MARKS.edge.dash[faction] ?? [];
}

/** How many factions the shader's dash table holds: `uPalette[8]`'s own. */
export const DASH_SLOTS = 8;

/**
 * The dash table as both territory borders read it (§4.3, R1577): two vec4
 * a faction, in the slots of `uPalette`.
 *
 * A pattern becomes up to three ON intervals, each as its middle and its
 * half length along the line, and the period. `A = (m0, h0, m1, h1)` and
 * `B = (m2, h2, period, 0)`. An unused interval carries a half length of
 * -1, which the shader reads as no dash at all, and a period of 0 is a
 * solid line.
 */
export function dashUniforms(): { a: Float32Array; b: Float32Array } {
  const a = new Float32Array(DASH_SLOTS * 4);
  const b = new Float32Array(DASH_SLOTS * 4);
  for (let f = 0; f < DASH_SLOTS; f++) {
    const runs = dashRuns(dashPattern(f));
    const mids = [0, 0, 0], halves = [-1, -1, -1];
    for (let i = 0; i < runs.on.length && i < 3; i++) {
      const [s, e] = runs.on[i];
      mids[i] = (s + e) / 2;
      halves[i] = (e - s) / 2;
    }
    a.set([mids[0], halves[0], mids[1], halves[1]], f * 4);
    b.set([mids[2], halves[2], runs.period, 0], f * 4);
  }
  return { a, b };
}

/**
 * The ON intervals of one period of a pattern, and the period.
 *
 * The pattern is on and off lengths in pairs, as `setLineDash` takes them;
 * an empty pattern has a period of 0 and no interval.
 */
export function dashRuns(pattern: readonly number[]): { on: [number, number][]; period: number } {
  const on: [number, number][] = [];
  let at = 0;
  for (let i = 0; i + 1 < pattern.length; i += 2) {
    on.push([at, at + pattern[i]]);
    at += pattern[i] + pattern[i + 1];
  }
  return { on, period: at };
}

/**
 * How far along the line a point `s` stands from the nearest dash of a
 * pattern, 0 inside one. It is the arithmetic of the composite's
 * `dashGap`, written out so a test can read the pattern a border draws.
 */
export function dashGap(s: number, pattern: readonly number[]): number {
  const { on, period } = dashRuns(pattern);
  if (period <= 0) return 0;
  const u = s - period * Math.floor(s / period);
  let best = Infinity;
  for (const [a, e] of on) {
    const mid = (a + e) / 2, half = (e - a) / 2;
    let d = u - mid;
    d -= period * Math.round(d / period);
    best = Math.min(best, Math.max(Math.abs(d) - half, 0));
  }
  return best;
}

/**
 * The stretch the traced outline puts on one loop before it lays the
 * pattern along it (R1577): a whole number of periods, at least one, fills
 * the loop, so the pattern ends where it began. A solid pattern takes no
 * stretch. It is the arithmetic of the outline stroke's vertex shader
 * (`fast/shaders/geo.ts`), written out so a test can read it; `loopLen` and
 * `period` are in one unit.
 */
export function dashLoopFit(loopLen: number, period: number): number {
  if (period <= 0) return 1;
  const len = Math.max(loopLen, 1e-3);
  return (Math.max(Math.floor(len / period + 0.5), 1) * period) / len;
}

/**
 * The dashes of a straight sample `length` long, from its left end: the
 * key draws them (card 05). Each run is clipped to the sample, and a run
 * of zero length is a dot that the round cap draws.
 */
export function dashSample(pattern: readonly number[], length: number): [number, number][] {
  const { on, period } = dashRuns(pattern);
  if (period <= 0) return [[0, length]];
  const out: [number, number][] = [];
  for (let base = 0; base < length; base += period) {
    for (const [a, e] of on) {
      const s = base + a;
      if (s > length) break;
      out.push([s, Math.min(length, base + e)]);
    }
  }
  return out;
}
